import { useState } from 'react';
import { Avatar } from '../../components/Avatar.tsx';
import {
  Alert,
  Badge,
  Button,
  EmptyState,
  Icons,
  PageHeader,
  Spinner,
  cx,
} from '../../components/ui.tsx';
import { formatDateTime, formatRelative } from '../../lib/display.ts';
import { useAuditLog, useAuditVerification, type AuditEvent } from '../../lib/profile-queries.ts';
import { describeAddress, parseUserAgent } from '../../lib/user-agent.ts';
import {
  CATEGORY_ORDER,
  describeChanges,
  phraseFor,
  prefixesFor,
  type AuditTone,
} from './audit-phrasing.ts';

/**
 * Everything that has happened, in order.
 *
 * Written for the person who has been handed a question — "who changed this?"
 * — rather than for whoever wrote the events. Each row reads as a sentence,
 * the technical detail is folded away until asked for, and the filters are
 * the categories a bookkeeper thinks in rather than the dotted prefixes the
 * code emits.
 */
export function AuditPage() {
  const [category, setCategory] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [cursors, setCursors] = useState<string[]>([]);

  const before = cursors.at(-1);
  const log = useAuditLog({
    // A category maps to one or more prefixes; the API takes a single one, so
    // multi-prefix categories filter on the client instead.
    ...(category && prefixesFor(category).length === 1
      ? { action: prefixesFor(category)[0] as string }
      : {}),
    ...(query ? { q: query } : {}),
    ...(before ? { before } : {}),
  });

  const verification = useAuditVerification();

  const events = (log.data?.events ?? []).filter((event) =>
    category ? phraseFor(event.action).category === category : true,
  );

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="Audit log"
        description="Every action anyone has taken, newest first. Records are added and never edited or removed — the log is sealed by the database itself."
      />

      <div className="mt-3 shrink-0 space-y-3">
        <ChainStatus verification={verification.data} loading={verification.isLoading} />

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted">
              <Icons.Search size={16} />
            </span>
            <input
              type="search"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setCursors([]);
              }}
              placeholder="Search by person or action…"
              aria-label="Search the audit log"
              className="h-control w-72 rounded-lg border border-border-input bg-surface pl-9 pr-3 text-body-sm text-ink"
            />
          </div>

          <div className="flex flex-wrap gap-1.5">
            <FilterChip active={category === null} onClick={() => setCategory(null)}>
              Everything
            </FilterChip>
            {CATEGORY_ORDER.map((name) => (
              <FilterChip
                key={name}
                active={category === name}
                onClick={() => {
                  setCategory(name);
                  setCursors([]);
                }}
              >
                {name}
              </FilterChip>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-3 min-h-0 flex-1 overflow-y-auto pb-6">
        {log.isLoading ? (
          <div className="flex items-center gap-2 py-12 text-body-sm text-muted">
            <Spinner /> Loading the log…
          </div>
        ) : log.isError ? (
          <Alert>Could not load the audit log.</Alert>
        ) : events.length === 0 ? (
          <EmptyState
            title="Nothing here yet"
            description={
              query || category
                ? 'No events match that filter. Try widening it.'
                : 'Actions appear here as people use the system.'
            }
          />
        ) : (
          <>
            <ol className="overflow-hidden rounded-xl border border-border-strong bg-surface">
              {events.map((event) => (
                <EventRow key={event.id} event={event} />
              ))}
            </ol>

            <div className="mt-3 flex items-center justify-between">
              <Button
                onClick={() => setCursors((list) => list.slice(0, -1))}
                disabled={cursors.length === 0}
              >
                ← Newer
              </Button>
              <span className="text-micro text-muted">
                {cursors.length > 0 ? `Page ${cursors.length + 1}` : 'Most recent'}
              </span>
              <Button
                onClick={() =>
                  setCursors((list) =>
                    log.data?.nextCursor ? [...list, log.data.nextCursor] : list,
                  )
                }
                disabled={!log.data?.nextCursor}
              >
                Older →
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Whether the chain still verifies.
 *
 * The append-only guarantee is worth stating plainly, because the whole
 * reason to keep a hash-chained log is so somebody can be told "nothing has
 * been rewritten" and have it mean something.
 */
function ChainStatus({
  verification,
  loading,
}: {
  verification:
    | { intact: true; checked: number }
    | { intact: false; brokenAt: string; reason: string }
    | undefined;
  loading: boolean;
}) {
  if (loading || !verification) return null;

  if (!verification.intact) {
    return (
      <Alert>
        <strong>This log has been tampered with.</strong> The chain first disagrees with itself at
        entry {verification.brokenAt} — {verification.reason}. Every record from that point on
        should be treated as unreliable.
      </Alert>
    );
  }

  return (
    <div className="flex items-center gap-2 rounded-lg border border-border bg-sunken px-3.5 py-2 text-meta text-body">
      <span className="text-[var(--success)]" aria-hidden>
        <Icons.Check size={16} />
      </span>
      <span>
        <strong className="font-medium text-ink">Log verified.</strong> All{' '}
        {verification.checked.toLocaleString('en-GB')} entries are intact and in order — each one is
        sealed against the one before it, so nothing can be changed or removed without showing.
      </span>
    </div>
  );
}

function EventRow({ event }: { event: AuditEvent }) {
  const [open, setOpen] = useState(false);
  const phrase = phraseFor(event.action);
  const changes = describeChanges(event.changes);
  const agent = parseUserAgent(event.userAgent);

  const hasDetail = changes.length > 0 || Boolean(event.ipAddress) || Boolean(event.entityId);

  return (
    <li className="border-b border-border last:border-0">
      <div className="flex items-start gap-3 px-4 py-3">
        <Avatar
          name={event.actor?.name ?? 'System'}
          userId={event.actor?.id}
          hasAvatar={event.actor?.hasAvatar ?? false}
          size="sm"
          className="mt-0.5"
        />

        <div className="min-w-0 flex-1">
          <p className="text-body-sm text-body">
            <span className="font-medium text-ink">{event.actor?.name ?? 'The system'}</span>{' '}
            <ToneText tone={phrase.tone}>{phrase.verb}</ToneText>
            {event.entityType ? (
              <span className="text-muted"> · {event.entityType.toLowerCase()}</span>
            ) : null}
          </p>

          <p className="mt-0.5 text-micro text-muted">
            {formatDateTime(event.createdAt)} · {formatRelative(event.createdAt)}
            {event.ipAddress ? ` · ${describeAddress(event.ipAddress)}` : ''}
          </p>

          {open ? (
            <div className="mt-2.5 rounded-lg border border-border bg-sunken px-3.5 py-3">
              {phrase.meaning ? (
                <p className="mb-2.5 text-meta text-body">{phrase.meaning}</p>
              ) : null}

              <dl className="grid gap-x-6 gap-y-1 text-meta sm:grid-cols-2">
                {changes.map((change) => (
                  <div key={change.label} className="flex gap-2">
                    <dt className="shrink-0 text-muted">{change.label}</dt>
                    <dd className="min-w-0 break-words text-body">{change.value}</dd>
                  </div>
                ))}
                {event.actor ? (
                  <Detail label="Account" value={event.actor.email} />
                ) : (
                  <Detail label="Account" value="No signed-in user — an automated action" />
                )}
                {event.ipAddress ? (
                  <Detail label="Address" value={describeAddress(event.ipAddress)} />
                ) : null}
                {event.userAgent ? <Detail label="Device" value={agent.summary} /> : null}
                {event.entityId ? <Detail label="Record" value={event.entityId} /> : null}
                <Detail label="Entry" value={`#${event.sequence}`} />
                <Detail label="Action code" value={event.action} />
              </dl>
            </div>
          ) : null}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Badge
            tone={
              phrase.tone === 'positive'
                ? 'success'
                : phrase.tone === 'neutral'
                  ? 'neutral'
                  : phrase.tone
            }
          >
            {phrase.category}
          </Badge>
          {hasDetail ? (
            <button
              type="button"
              onClick={() => setOpen((value) => !value)}
              aria-expanded={open}
              className="rounded-md p-1 text-muted transition-fast hover:bg-sunken hover:text-ink"
              aria-label={open ? 'Hide the details' : 'Show the details'}
            >
              {open ? <Icons.ChevronDown size={16} /> : <Icons.ChevronRight size={16} />}
            </button>
          ) : null}
        </div>
      </div>
    </li>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2">
      <dt className="shrink-0 text-muted">{label}</dt>
      <dd className="min-w-0 break-all text-body">{value}</dd>
    </div>
  );
}

function ToneText({ tone, children }: { tone: AuditTone; children: string }) {
  return (
    <span
      className={cx(
        tone === 'danger' && 'font-medium text-[var(--danger)]',
        tone === 'warning' && 'font-medium text-[var(--warning)]',
        tone === 'positive' && 'text-[var(--success)]',
      )}
    >
      {children}
    </span>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cx(
        'rounded-full border px-3 py-1.5 text-meta transition-fast',
        active
          ? 'border-[var(--blue-600)] bg-blue-600 font-medium text-white'
          : 'border-border-input bg-surface text-body hover:bg-sunken',
      )}
    >
      {children}
    </button>
  );
}
